import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  MapPin,
  FileText,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Globe,
  Phone,
  Mail,
  Building,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { StatusBadge, DataTable, Column, MetricCard, ConfirmDialog } from '@/components/common';
import {
  tmsCarriersApi,
  TmsCarrier,
  TmsCarrierLane,
  TmsCarrierDocument,
  CreateCarrierLaneDto,
  CreateCarrierDocumentDto,
  CarrierPerformanceReport,
  ServiceType,
  CarrierDocumentType,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { format } from 'date-fns';

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXPEDITED', label: 'Expedited' },
  { value: 'OVERNIGHT', label: 'Overnight' },
  { value: 'SAME_DAY', label: 'Same Day' },
  { value: 'ECONOMY', label: 'Economy' },
  { value: 'DEDICATED', label: 'Dedicated' },
  { value: 'TEMPERATURE_CONTROLLED', label: 'Temperature Controlled' },
  { value: 'HAZMAT', label: 'Hazmat' },
  { value: 'OVERSIZED', label: 'Oversized' },
];

const DOCUMENT_TYPES: { value: CarrierDocumentType; label: string }[] = [
  { value: 'INSURANCE_CERTIFICATE', label: 'Insurance Certificate' },
  { value: 'OPERATING_AUTHORITY', label: 'Operating Authority' },
  { value: 'W9', label: 'W-9 Form' },
  { value: 'SAFETY_RATING', label: 'Safety Rating' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'RATE_AGREEMENT', label: 'Rate Agreement' },
  { value: 'OTHER', label: 'Other' },
];

export default function CarrierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [carrier, setCarrier] = useState<TmsCarrier | null>(null);
  const [performance, setPerformance] = useState<CarrierPerformanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Lane form state
  const [laneFormOpen, setLaneFormOpen] = useState(false);
  const [editingLane, setEditingLane] = useState<TmsCarrierLane | null>(null);
  const [laneFormData, setLaneFormData] = useState<CreateCarrierLaneDto>({
    originState: '',
    originCity: '',
    destinationState: '',
    destinationCity: '',
    serviceType: 'STANDARD',
    transitDaysMin: undefined,
    transitDaysMax: undefined,
  });
  const [savingLane, setSavingLane] = useState(false);
  const [deleteLaneDialog, setDeleteLaneDialog] = useState<{ open: boolean; lane?: TmsCarrierLane }>({
    open: false,
  });

  // Document form state
  const [docFormOpen, setDocFormOpen] = useState(false);
  const [docFormData, setDocFormData] = useState<CreateCarrierDocumentDto>({
    type: 'INSURANCE_CERTIFICATE',
    name: '',
    documentNumber: '',
    expiryDate: '',
    documentUrl: '',
    notes: '',
  });
  const [savingDoc, setSavingDoc] = useState(false);
  const [deleteDocDialog, setDeleteDocDialog] = useState<{ open: boolean; doc?: TmsCarrierDocument }>({
    open: false,
  });

  const fetchCarrier = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await tmsCarriersApi.getById(id);
      setCarrier(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/carriers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformance = async () => {
    if (!id) return;
    try {
      const data = await tmsCarriersApi.getPerformance(id);
      setPerformance(data);
    } catch (error) {
      console.error('Failed to fetch performance:', error);
    }
  };

  useEffect(() => {
    fetchCarrier();
    fetchPerformance();
  }, [id]);

  // Lane handlers
  const handleOpenLaneForm = (lane?: TmsCarrierLane) => {
    if (lane) {
      setEditingLane(lane);
      setLaneFormData({
        originState: lane.originState || '',
        originCity: lane.originCity || '',
        destinationState: lane.destinationState || '',
        destinationCity: lane.destinationCity || '',
        serviceType: lane.serviceType,
        transitDaysMin: lane.transitDaysMin || undefined,
        transitDaysMax: lane.transitDaysMax || undefined,
      });
    } else {
      setEditingLane(null);
      setLaneFormData({
        originState: '',
        originCity: '',
        destinationState: '',
        destinationCity: '',
        serviceType: 'STANDARD',
      });
    }
    setLaneFormOpen(true);
  };

  const handleSaveLane = async () => {
    if (!id) return;
    try {
      setSavingLane(true);
      if (editingLane) {
        await tmsCarriersApi.updateLane(editingLane.id, laneFormData);
        toast.success('Lane updated');
      } else {
        await tmsCarriersApi.addLane(id, laneFormData);
        toast.success('Lane added');
      }
      setLaneFormOpen(false);
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingLane(false);
    }
  };

  const handleDeleteLane = async () => {
    if (!id || !deleteLaneDialog.lane) return;
    try {
      await tmsCarriersApi.removeLane(id, deleteLaneDialog.lane.id);
      toast.success('Lane removed');
      setDeleteLaneDialog({ open: false });
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Document handlers
  const handleSaveDoc = async () => {
    if (!id) return;
    try {
      setSavingDoc(true);
      await tmsCarriersApi.addDocument(id, docFormData);
      toast.success('Document added');
      setDocFormOpen(false);
      setDocFormData({
        type: 'INSURANCE_CERTIFICATE',
        name: '',
        documentNumber: '',
        expiryDate: '',
        documentUrl: '',
        notes: '',
      });
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDoc(false);
    }
  };

  const handleVerifyDoc = async (docId: string) => {
    try {
      await tmsCarriersApi.verifyDocument(docId);
      toast.success('Document verified');
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteDoc = async () => {
    if (!id || !deleteDocDialog.doc) return;
    try {
      await tmsCarriersApi.removeDocument(id, deleteDocDialog.doc.id);
      toast.success('Document removed');
      setDeleteDocDialog({ open: false });
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Status handlers
  const handleApprove = async () => {
    if (!id) return;
    try {
      await tmsCarriersApi.approve(id);
      toast.success('Carrier approved');
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSuspend = async () => {
    if (!id) return;
    try {
      await tmsCarriersApi.suspend(id);
      toast.success('Carrier suspended');
      fetchCarrier();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!carrier) {
    return (
      <div className="p-6">
        <p>Carrier not found</p>
      </div>
    );
  }

  const laneColumns: Column<TmsCarrierLane>[] = [
    {
      key: 'origin',
      header: 'Origin',
      render: (lane) => (
        <div>
          <p className="font-medium">{lane.originCity || 'Any'}, {lane.originState || 'Any'}</p>
          {lane.originZipRange && (
            <p className="text-xs text-muted-foreground">ZIP: {lane.originZipRange}</p>
          )}
        </div>
      ),
    },
    {
      key: 'destination',
      header: 'Destination',
      render: (lane) => (
        <div>
          <p className="font-medium">{lane.destinationCity || 'Any'}, {lane.destinationState || 'Any'}</p>
          {lane.destinationZipRange && (
            <p className="text-xs text-muted-foreground">ZIP: {lane.destinationZipRange}</p>
          )}
        </div>
      ),
    },
    {
      key: 'serviceType',
      header: 'Service',
      render: (lane) => (
        <Badge variant="outline">
          {SERVICE_TYPES.find((s) => s.value === lane.serviceType)?.label || lane.serviceType}
        </Badge>
      ),
    },
    {
      key: 'transit',
      header: 'Transit Days',
      render: (lane) =>
        lane.transitDaysMin || lane.transitDaysMax ? (
          <span>
            {lane.transitDaysMin ?? '?'} - {lane.transitDaysMax ?? '?'} days
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (lane) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => handleOpenLaneForm(lane)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteLaneDialog({ open: true, lane })}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const docColumns: Column<TmsCarrierDocument>[] = [
    {
      key: 'name',
      header: 'Document',
      render: (doc) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{doc.name}</p>
            <p className="text-xs text-muted-foreground">
              {DOCUMENT_TYPES.find((t) => t.value === doc.type)?.label}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'documentNumber',
      header: 'Number',
      render: (doc) => doc.documentNumber || '-',
    },
    {
      key: 'expiryDate',
      header: 'Expires',
      render: (doc) => {
        if (!doc.expiryDate) return '-';
        const expiry = new Date(doc.expiryDate);
        const isExpired = expiry < new Date();
        const isExpiring = expiry < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        return (
          <span className={isExpired ? 'text-red-600' : isExpiring ? 'text-amber-600' : ''}>
            {format(expiry, 'MMM d, yyyy')}
          </span>
        );
      },
    },
    {
      key: 'isVerified',
      header: 'Verified',
      render: (doc) =>
        doc.isVerified ? (
          <Badge variant="outline" className="text-emerald-600">
            <CheckCircle className="mr-1 h-3 w-3" /> Verified
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-600">
            <AlertTriangle className="mr-1 h-3 w-3" /> Pending
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (doc) => (
        <div className="flex gap-1">
          {!doc.isVerified && (
            <Button variant="ghost" size="sm" onClick={() => handleVerifyDoc(doc.id)}>
              Verify
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteDocDialog({ open: true, doc })}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/carriers')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Truck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{carrier.name}</h1>
              <p className="text-muted-foreground">
                {carrier.code} {carrier.scac && `| SCAC: ${carrier.scac}`}
              </p>
            </div>
            <StatusBadge
              status={carrier.status.toLowerCase().replace('_', '-') as any}
              label={carrier.status.replace('_', ' ')}
            />
          </div>
        </div>
        <div className="flex gap-2">
          {carrier.status === 'PENDING_APPROVAL' && (
            <Button onClick={handleApprove}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </Button>
          )}
          {carrier.status === 'ACTIVE' && (
            <Button variant="outline" onClick={handleSuspend}>
              <XCircle className="mr-2 h-4 w-4" />
              Suspend
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Service Lanes"
          value={carrier._count?.lanes || 0}
          icon={<MapPin className="h-5 w-5" />}
        />
        <MetricCard
          label="Documents"
          value={carrier._count?.documents || 0}
          icon={<FileText className="h-5 w-5" />}
        />
        <MetricCard
          label="On-Time Delivery"
          value={carrier.onTimeDeliveryRate ? `${carrier.onTimeDeliveryRate.toFixed(1)}%` : '-'}
          trend={
            carrier.onTimeDeliveryRate
              ? {
                  direction: carrier.onTimeDeliveryRate >= 95 ? 'up' : 'down',
                  value: '',
                  text: carrier.onTimeDeliveryRate >= 95 ? 'Excellent' : 'Needs attention',
                }
              : undefined
          }
        />
        <MetricCard
          label="Overall Rating"
          value={carrier.overallRating?.toFixed(1) || '-'}
          icon={<BarChart3 className="h-5 w-5" />}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="lanes">Service Lanes ({carrier._count?.lanes || 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({carrier._count?.documents || 0})</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {carrier.primaryContact && (
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{carrier.primaryContact}</span>
                  </div>
                )}
                {carrier.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${carrier.email}`} className="text-primary hover:underline">
                      {carrier.email}
                    </a>
                  </div>
                )}
                {carrier.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{carrier.phone}</span>
                  </div>
                )}
                {carrier.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={carrier.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {carrier.website}
                    </a>
                  </div>
                )}
                {carrier.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {[carrier.address.street, carrier.address.city, carrier.address.state, carrier.address.zipCode]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Identification */}
            <Card>
              <CardHeader>
                <CardTitle>Identification</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">MC Number</p>
                    <p className="font-medium">{carrier.mcNumber || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">DOT Number</p>
                    <p className="font-medium">{carrier.dotNumber || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">SCAC Code</p>
                    <p className="font-medium">{carrier.scac || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tax ID</p>
                    <p className="font-medium">{carrier.taxId || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Capabilities */}
            <Card>
              <CardHeader>
                <CardTitle>Capabilities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Service Types</p>
                  <div className="flex flex-wrap gap-2">
                    {carrier.serviceTypes.map((type) => (
                      <Badge key={type} variant="secondary">
                        {SERVICE_TYPES.find((s) => s.value === type)?.label || type}
                      </Badge>
                    ))}
                  </div>
                </div>
                {carrier.equipmentTypes.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Equipment Types</p>
                    <div className="flex flex-wrap gap-2">
                      {carrier.equipmentTypes.map((type) => (
                        <Badge key={type} variant="outline">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-4">
                  {carrier.hazmatCertified && (
                    <Badge variant="secondary" className="text-amber-600">
                      Hazmat Certified
                    </Badge>
                  )}
                  {carrier.temperatureControlled && (
                    <Badge variant="secondary" className="text-blue-600">
                      Temperature Controlled
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Financial */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Terms</p>
                    <p className="font-medium">Net {carrier.paymentTermsDays} Days</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Credit Limit</p>
                    <p className="font-medium">
                      {carrier.creditLimit ? `$${carrier.creditLimit.toLocaleString()}` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Insurance Minimum</p>
                    <p className="font-medium">
                      {carrier.insuranceMinimum ? `$${carrier.insuranceMinimum.toLocaleString()}` : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Lanes Tab */}
        <TabsContent value="lanes" className="space-y-4">
          <div className="flex justify-between">
            <p className="text-muted-foreground">Define the service lanes this carrier operates</p>
            <Button onClick={() => handleOpenLaneForm()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Lane
            </Button>
          </div>
          <DataTable columns={laneColumns} data={carrier.lanes || []} />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex justify-between">
            <p className="text-muted-foreground">Manage carrier compliance documents</p>
            <Button onClick={() => setDocFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Document
            </Button>
          </div>
          <DataTable columns={docColumns} data={carrier.documents || []} />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          {performance?.aggregate ? (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Total Shipments" value={performance.aggregate.totalShipments} />
                <MetricCard
                  label="Pickup Performance"
                  value={performance.aggregate.pickupPerformance ? `${performance.aggregate.pickupPerformance}%` : '-'}
                />
                <MetricCard
                  label="Delivery Performance"
                  value={performance.aggregate.deliveryPerformance ? `${performance.aggregate.deliveryPerformance}%` : '-'}
                />
                <MetricCard
                  label="Damage Rate"
                  value={performance.aggregate.damageRate ? `${performance.aggregate.damageRate}%` : '-'}
                />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Performance</CardTitle>
                  <CardDescription>Last 12 months of performance data</CardDescription>
                </CardHeader>
                <CardContent>
                  {performance.monthlyRecords.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="py-2 text-left">Month</th>
                            <th className="py-2 text-right">Shipments</th>
                            <th className="py-2 text-right">On-Time Pickups</th>
                            <th className="py-2 text-right">On-Time Deliveries</th>
                            <th className="py-2 text-right">Damage Incidents</th>
                          </tr>
                        </thead>
                        <tbody>
                          {performance.monthlyRecords.map((record) => (
                            <tr key={record.id} className="border-b">
                              <td className="py-2">{format(new Date(record.periodMonth), 'MMM yyyy')}</td>
                              <td className="py-2 text-right">{record.totalShipments}</td>
                              <td className="py-2 text-right">
                                {record.onTimePickups} / {record.onTimePickups + record.latePickups}
                              </td>
                              <td className="py-2 text-right">
                                {record.onTimeDeliveries} / {record.onTimeDeliveries + record.lateDeliveries}
                              </td>
                              <td className="py-2 text-right">{record.damageIncidents}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No performance data available</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No performance data available yet</p>
                <p className="text-sm text-muted-foreground">Performance metrics will appear once shipments are completed</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Lane Form Sheet */}
      <Sheet open={laneFormOpen} onOpenChange={setLaneFormOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingLane ? 'Edit Lane' : 'Add Service Lane'}</SheetTitle>
            <SheetDescription>Define the origin and destination for this service lane</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Origin State</Label>
                <Input
                  value={laneFormData.originState || ''}
                  onChange={(e) => setLaneFormData({ ...laneFormData, originState: e.target.value })}
                  placeholder="TX"
                />
              </div>
              <div className="space-y-2">
                <Label>Origin City</Label>
                <Input
                  value={laneFormData.originCity || ''}
                  onChange={(e) => setLaneFormData({ ...laneFormData, originCity: e.target.value })}
                  placeholder="Houston"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Destination State</Label>
                <Input
                  value={laneFormData.destinationState || ''}
                  onChange={(e) => setLaneFormData({ ...laneFormData, destinationState: e.target.value })}
                  placeholder="OK"
                />
              </div>
              <div className="space-y-2">
                <Label>Destination City</Label>
                <Input
                  value={laneFormData.destinationCity || ''}
                  onChange={(e) => setLaneFormData({ ...laneFormData, destinationCity: e.target.value })}
                  placeholder="Oklahoma City"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Service Type</Label>
              <Select
                value={laneFormData.serviceType}
                onValueChange={(value) => setLaneFormData({ ...laneFormData, serviceType: value as ServiceType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transit Days (Min)</Label>
                <Input
                  type="number"
                  value={laneFormData.transitDaysMin || ''}
                  onChange={(e) =>
                    setLaneFormData({ ...laneFormData, transitDaysMin: e.target.value ? parseInt(e.target.value) : undefined })
                  }
                  placeholder="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Transit Days (Max)</Label>
                <Input
                  type="number"
                  value={laneFormData.transitDaysMax || ''}
                  onChange={(e) =>
                    setLaneFormData({ ...laneFormData, transitDaysMax: e.target.value ? parseInt(e.target.value) : undefined })
                  }
                  placeholder="3"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setLaneFormOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveLane} disabled={savingLane}>
                {savingLane ? 'Saving...' : editingLane ? 'Update Lane' : 'Add Lane'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Document Form Dialog */}
      <Dialog open={docFormOpen} onOpenChange={setDocFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
            <DialogDescription>Upload or link a carrier document</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={docFormData.type}
                onValueChange={(value) => setDocFormData({ ...docFormData, type: value as CarrierDocumentType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Document Name</Label>
              <Input
                value={docFormData.name}
                onChange={(e) => setDocFormData({ ...docFormData, name: e.target.value })}
                placeholder="Certificate of Insurance 2024"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Number</Label>
                <Input
                  value={docFormData.documentNumber || ''}
                  onChange={(e) => setDocFormData({ ...docFormData, documentNumber: e.target.value })}
                  placeholder="POL-123456"
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={docFormData.expiryDate || ''}
                  onChange={(e) => setDocFormData({ ...docFormData, expiryDate: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Document URL</Label>
              <Input
                value={docFormData.documentUrl || ''}
                onChange={(e) => setDocFormData({ ...docFormData, documentUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocFormOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDoc} disabled={savingDoc || !docFormData.name}>
              {savingDoc ? 'Saving...' : 'Add Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lane Confirmation */}
      <ConfirmDialog
        open={deleteLaneDialog.open}
        onOpenChange={(open) => setDeleteLaneDialog({ open })}
        title="Remove Lane"
        description="Are you sure you want to remove this service lane?"
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleDeleteLane}
      />

      {/* Delete Document Confirmation */}
      <ConfirmDialog
        open={deleteDocDialog.open}
        onOpenChange={(open) => setDeleteDocDialog({ open })}
        title="Remove Document"
        description={`Are you sure you want to remove "${deleteDocDialog.doc?.name}"?`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleDeleteDoc}
      />
    </div>
  );
}
